import { Typography } from '@mui/material';
import { spotterImageBlue, spotterImageYellow } from '@sofarocean/atlas/images';
import { chain, compact, map, partition } from 'lodash';
import {
    PropsWithChildren,
    ReactNode,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

import { useDeviceIdList } from '@/api/deviceIdList';
import { useLeaderboard } from '@/api/leaderboard/useLeaderboard';
import { FleetDeviceLatestData, useFleetSpottersLatestData } from '@/api/oceansApi/useFleetSpottersLatestData';
import { Datum } from '@/components/data/Datum';
import { useSpotterHash } from '@/hooks/useSpotterHash';
import { boldWeight, minorTextGrey } from '@/theme';

const ADVANCE_INTERVAL_MS = 10_000;
const IDLE_RESUME_MS = 15_000;

export type OverallStatEntry = {
    stat: 'overall';
    title: string;
    spotterIds: string[];
    icons: string[];
    subtitle?: string;
    body: ReactNode;
};

export type TopSpotTourEntry = ReturnType<typeof useLeaderboard>[number] | OverallStatEntry;

type TopSpotTourContextValue = {
    entries: TopSpotTourEntry[];
    /** Index into entries[] that drives focused card styling. Same spotter can appear multiple times; only this index is highlighted. */
    focusedEntry: TopSpotTourEntry | null;
    /** Manually focus a specific entry by its original index in entries[]. Also pauses the tour timer. */
    selectEntry: (index: number) => void;
    /** Call on user map interaction (pan/zoom) to pause the tour timer. */
    onUserInteraction: () => void;
};

const TopSpotTourContext = createContext<TopSpotTourContextValue>({
    entries: [],
    focusedEntry: null,
    selectEntry: () => {},
    onUserInteraction: () => {},
});

export const TopSpotTourContextProvider = ({ children }: PropsWithChildren) => {
    const leaders = useLeaderboard();
    const { data: deviceIdItems } = useDeviceIdList({ adminAllDevices: true });
    const spotterIds = useMemo(() => deviceIdItems?.data?.devices?.map((d) => d.spotterId) ?? [], [deviceIdItems]);
    const { data: fleetData } = useFleetSpottersLatestData({ spotters: spotterIds, useCache: true });

    const { spotterId } = useSpotterHash();

    const [focusedIndex, setFocusedIndex] = useState(0);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const idleTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const entries = useMemo(() => {
        const overallEntries = collectOverallEntries(fleetData?.data);
        if (!overallEntries.length) return leaders;
        const result = [...leaders] as ((typeof leaders)[number] | OverallStatEntry)[];
        const step = leaders.length / (overallEntries.length + 1);
        for (let i = 0; i < overallEntries.length; i++) {
            result.splice(Math.round((i + 1) * step) + i, 0, overallEntries[i]);
        }
        return result;
    }, [leaders, fleetData]);
    // Ref so the interval callback always sees current length without restarting.
    const entriesLengthRef = useRef(entries.length);
    useEffect(() => {
        entriesLengthRef.current = entries.length;
    }, [entries.length]);

    const stopInterval = useCallback(() => {
        if (!intervalRef.current) return;
        clearInterval(intervalRef.current);
        intervalRef.current = null;
    }, []);

    const startInterval = useCallback(() => {
        if (intervalRef.current) return;
        intervalRef.current = setInterval(() => {
            const len = entriesLengthRef.current;
            if (len === 0) return;
            setFocusedIndex((i) => {
                return (i + 1) % len;
            });
        }, ADVANCE_INTERVAL_MS);
    }, []);

    const pauseAndScheduleResume = useCallback(() => {
        stopInterval();
        if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
        idleTimeoutRef.current = setTimeout(startInterval, IDLE_RESUME_MS);
    }, [startInterval, stopInterval]);

    const selectEntry = useCallback(
        (index: number) => {
            setFocusedIndex(index);
            pauseAndScheduleResume();
        },
        [pauseAndScheduleResume]
    );

    useEffect(() => {
        if (!spotterId) return;
        const focusedEntry = entries[focusedIndex];
        if (focusedEntry && 'spotterId' in focusedEntry && spotterId === focusedEntry.spotterId) {
            // if we're already looking at the right spotter, this is probably just a feedback loop after the hash update
            return;
        }

        const index = entries.findIndex((e) => 'spotterId' in e && e.spotterId === spotterId);
        if (index !== -1) selectEntry(index);
    }, [spotterId]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        startInterval();
        return () => {
            stopInterval();
            if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
        };
    }, [startInterval, stopInterval]);

    return (
        <TopSpotTourContext.Provider
            value={{
                entries,
                focusedEntry: entries[focusedIndex],
                selectEntry,
                onUserInteraction: pauseAndScheduleResume,
            }}
        >
            {children}
        </TopSpotTourContext.Provider>
    );
};

const collectOverallEntries = (fleet?: FleetDeviceLatestData[]): OverallStatEntry[] => {
    if (!fleet?.length) {
        return [];
    }
    const [grid, nonGrid] = partition(fleet, ({ owner }) => owner === 'grid');
    const [maxNonGridOwner, maxNonGridDevices] = chain(nonGrid)
        .groupBy('owner')
        .omit('spoondrift')
        .entries()
        .maxBy(([, spotters]) => spotters.length)
        .value() as [string, FleetDeviceLatestData[]];
    return compact([
        {
            stat: 'overall',
            title: 'The Fleet',
            subtitle: 'Active in the last 24h',
            icons: [spotterImageBlue, spotterImageYellow],
            spotterIds: map(fleet, 'spotterId'),
            body: (
                <Typography sx={{ fontSize: '1.25em', fontWeight: boldWeight }}>
                    <Datum variant="inherit" value={fleet.length} type="unitless" /> Spotters
                </Typography>
            ),
        },
        {
            stat: 'overall',
            title: 'The Grid',
            subtitle: 'Active in the last 24h (including beached)',
            icons: [spotterImageBlue],
            spotterIds: map(grid, 'spotterId'),
            body: (
                <Typography sx={{ fontSize: '1.25em', fontWeight: boldWeight }}>
                    <Datum variant="inherit" value={grid.length} type="unitless" /> Spotters
                </Typography>
            ),
        },
        {
            stat: 'overall',
            title: 'Customer Spotters',
            subtitle: 'Active in the last 24h',
            icons: [spotterImageYellow],
            spotterIds: map(nonGrid, 'spotterId'),
            body: (
                <Typography sx={{ fontSize: '1.25em', fontWeight: boldWeight }}>
                    <Datum variant="inherit" value={nonGrid.length} type="unitless" /> Spotters
                </Typography>
            ),
        },
        maxNonGridOwner &&
            maxNonGridDevices && {
                stat: 'overall',
                title: 'Elite Fleet',
                subtitle: 'Active in the last 24h',
                icons: [spotterImageYellow],
                spotterIds: map(maxNonGridDevices, 'spotterId'),
                body: (
                    <>
                        <Typography sx={{ fontSize: '1.25em', fontWeight: boldWeight }}>
                            <Datum variant="inherit" value={maxNonGridDevices.length} type="unitless" /> Spotters
                        </Typography>
                        <Typography
                            sx={{
                                color: minorTextGrey,
                                fontSize: '0.5em',
                                transition: '400ms',
                                display: 'none',
                                opacity: 0,
                                '.focused &': {
                                    display: 'initial',
                                    opacity: 1,
                                },
                            }}
                        >
                            {maxNonGridOwner}
                        </Typography>
                    </>
                ),
            },
    ]);
};

export const useTopSpotTourContext = () => useContext(TopSpotTourContext);
